import React, { useState } from 'react';
import { ScrollView } from 'react-native';
import { useDispatch } from 'react-redux';
import { Auth as Authorization } from 'aws-amplify';
import * as LoginActions from '../../redux/actions/loginAction';
import * as AppActions from '../../redux/actions/appAction';
import { validateEmail, validatePhoneNumber, formatPhoneNumberForAmplify } from 'src/utils/utils';
import { Box, Button, FormControl, IconButton, Input, VStack, Text } from 'native-base';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { containerStyle } from 'src/styles/containerStyle';
import { inputStyle } from 'src/styles/inputStyle';
import ProgressBar from 'src/components/ProgressBar';
import InweonLogo from 'src/components/InweonLogo';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { formProps } from 'src/styles/formProps';
import { getText } from 'src/utils/i18n';
import { EXPLICIT_LOGOUT_STORAGE_KEY, LAST_GRAIN_PROFILE_STORAGE_KEY } from 'src/constants';

interface SignupFormState {
    username: string,
    password: string,
    email: string,
    phoneNumber: string,
    confirmationCode: string
}

// Enum for signup steps
enum SignupStep {
    SIGNUP_FORM = 'SIGNUP_FORM',
    CONFIRMATION = 'CONFIRMATION',
    COMPLETED = 'COMPLETED'
}

interface NavigationProp {
    navigate: (screen: string, params?: any) => void;
}

interface SignupPageProps {
    navigation: NavigationProp;
}

export default function SignupPage({ navigation }: SignupPageProps) {

    const [securePasswordEntry, setSecurePasswordEntry] = React.useState(true);
    const [currentStep, setCurrentStep] = useState<SignupStep>(SignupStep.SIGNUP_FORM);

    const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
    const [formData, setData] = React.useState<SignupFormState>({
        username: '',
        password: '',
        email: '',
        phoneNumber: '',
        confirmationCode: ''
    });

    const [errors, setErrors] = React.useState<{[key: string]: string}>({});
    const [resendMessage, setResendMessage] = React.useState('');
    const [resendCooldown, setResendCooldown] = React.useState(0);

    React.useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setTimeout(() => setResendCooldown(value => Math.max(0, value - 1)), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    const updateField = (field: keyof SignupFormState, value: string) => {
        setData({ ...formData, [field]: value });
        if (errors[field] || errors.formError) {
            setErrors({ ...errors, [field]: '', formError: '' });
        }
    };

    const getFriendlySignupError = (error: any) => {
        const code = String(error?.code || '');
        if (code === 'UsernameExistsException') return getText('accountAlreadyExists');
        if (code === 'InvalidPasswordException') return getText('passwordRequirements');
        if (code === 'CodeMismatchException') return getText('invalidConfirmationCode');
        if (code === 'ExpiredCodeException') return getText('expiredConfirmationCode');
        if (/network|fetch|service unavailable/i.test(String(error?.message || ''))) return getText('networkSignupError');
        return getText('signupUnexpectedError');
    };

    const validateSignupForm = () => {
        const nextErrors: {[key: string]: string} = {};

        if (!formData.username?.trim()) nextErrors.username = 'Username is required';
        if (!formData.password) {
            nextErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            nextErrors.password = 'Password must be at least 8 characters';
        }
        if (!formData.email?.trim()) {
            nextErrors.email = 'Email is required';
        } else if (!validateEmail(formData.email)) {
            nextErrors.email = 'Email is not valid';
        }
        if (!formData.phoneNumber?.trim()) {
            nextErrors.phoneNumber = 'Phone number is required';
        } else if (!validatePhoneNumber(formData.phoneNumber)) {
            nextErrors.phoneNumber = 'Phone number must be 10 digits (e.g., 9999******) or include country code (e.g., +919999******)';
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const validateConfirmationCode = () => {
        if (formData.confirmationCode === undefined
            || formData.confirmationCode.length === 0) {
            setErrors({
                confirmationCode: 'Confirmation code is required'
            });
            return false;
        }
        return true;
    };

    const validate = () => {
        if (currentStep === SignupStep.SIGNUP_FORM) {
            return validateSignupForm();
        } else if (currentStep === SignupStep.CONFIRMATION) {
            return validateConfirmationCode();
        }
        return false;
    };

    const dispatch = useDispatch();

    const resetState = async () => {
        try {
            await clearAppData();
            dispatch(AppActions.logout());
        } catch (e) {
            console.log('error signing out: ', e);
        }
    }

    const clearAppData = async function () {
        try {
            const lastProfile = await AsyncStorage.getItem(LAST_GRAIN_PROFILE_STORAGE_KEY);
            const keys = await AsyncStorage.getAllKeys();
            await AsyncStorage.multiRemove(keys);
            await AsyncStorage.clear();
            if (lastProfile) {
                await AsyncStorage.setItem(LAST_GRAIN_PROFILE_STORAGE_KEY, lastProfile);
            }
            console.log('App data cleared.');
        } catch (error) {
            console.error('Error clearing app data.');
        }
    }

    const handleSignup = async () => {
        setShowLoadingIndicator(true);
        setErrors({}); // Clear any previous errors
        
        try {
            // Prepare attributes object
            const attributes: any = {
                email: formData.email,
                phone_number: formatPhoneNumberForAmplify(formData.phoneNumber)
            };
            
            const signUpResult = await Authorization.signUp({
                username: formData.username,
                password: formData.password,
                attributes: attributes
            });
            
            console.log('Signup successful:', signUpResult);
            setShowLoadingIndicator(false);
            
            // Move to confirmation step instead of navigating to another page
            setCurrentStep(SignupStep.CONFIRMATION);
            
        } catch (ex: any) {
            console.error('Signup failed:', ex);
            setErrors({
                formError: getFriendlySignupError(ex)
            });
            setShowLoadingIndicator(false);
            dispatch(LoginActions.setUserLoggedIn("NOT_LOGGED_IN"));
        }
    }

    const handleConfirmSignup = async () => {
        setShowLoadingIndicator(true);
        setErrors({}); // Clear any previous errors
        
        try {
            console.log('confirming signup...');
            await Authorization.confirmSignUp(formData.username, formData.confirmationCode);
            console.log('confirmation success');
            
            // Reset state before login, similar to LoginPage
            await resetState();
            
            // Sign in the user automatically
            const loginResp = await Authorization.signIn(formData.username, formData.password);
            console.log('login after signup success', loginResp);
            
            if ('challengeName' in loginResp && loginResp['challengeName'] === "NEW_PASSWORD_REQUIRED") {
                // Handle force password change if needed
                console.log('New password required after signup confirmation');
                navigation.navigate('ForceChangePassword', {
                    user: loginResp
                });
                setShowLoadingIndicator(false);
                return;
            }
            
            if (loginResp['signInUserSession'] !== undefined) {
                await AsyncStorage.removeItem(EXPLICIT_LOGOUT_STORAGE_KEY);
                // Mark this as a new signup in AsyncStorage
                await AsyncStorage.setItem('isNewSignup', 'true');

                dispatch(LoginActions.setUserLoggedIn("LOGGED_IN"));
                console.log('user logged in after signup confirmation');

                // Don't hide loader or navigate - let the stack switch handle it
                // The app will switch to AppUserForGrainAnalysisStack which will
                // check the isNewSignup flag and route to ChooseGrainProfile
            } else {
                dispatch(LoginActions.setUserLoggedIn("NOT_LOGGED_IN"));
                setErrors({
                    formError: 'Login failed after signup confirmation'
                });
                setShowLoadingIndicator(false);
            }
        } catch (e: any) {
            console.error('Confirmation or login error:', e);
            setErrors({
                formError: getFriendlySignupError(e)
            });
            setShowLoadingIndicator(false);
            dispatch(LoginActions.setUserLoggedIn("NOT_LOGGED_IN"));
        }
    }

    const handleAccountExists = async () => {
        navigation.navigate('Login')
    }

    const handleBackToSignup = () => {
        setCurrentStep(SignupStep.SIGNUP_FORM);
        setErrors({});
    }

    const handleResendCode = async () => {
        setShowLoadingIndicator(true);
        setResendMessage('');
        setErrors({});
        try {
            await Authorization.resendSignUp(formData.username);
            setResendMessage(getText('confirmationCodeResent'));
            setResendCooldown(30);
        } catch (error) {
            setErrors({ formError: getFriendlySignupError(error) });
        } finally {
            setShowLoadingIndicator(false);
        }
    };

    const onSubmit = () => {
        const isValid = validate();
        if (isValid) {
            if (currentStep === SignupStep.SIGNUP_FORM) {
                handleSignup();
            } else if (currentStep === SignupStep.CONFIRMATION) {
                handleConfirmSignup();
            }
        }
    };

    const renderSignupForm = () => (
        <>
            <InweonLogo title={getText('signUpTitle')} />
            <VStack width={formProps.width}
                style={containerStyle.formContainer} mx="3">
                <FormControl isRequired>
                    <FormControl.Label>{getText('usernameLabel')}</FormControl.Label>
                    <Input
                        style={inputStyle.nativeInput}
                        isInvalid={'username' in errors}
                        placeholder={getText('usernamePlaceholder')}
                        onChangeText={value => updateField('username', value)} />
                    {'username' in errors ? <FormControl.ErrorMessage>{errors['username']}</FormControl.ErrorMessage> : null}

                    <FormControl.Label>{getText('passwordLabel')}</FormControl.Label>
                    <Input
                        secureTextEntry={securePasswordEntry}
                        style={inputStyle.nativeInput}
                        isInvalid={'password' in errors}
                        InputRightElement={
                            <IconButton
                                onPress={() => {
                                    setSecurePasswordEntry(!securePasswordEntry)
                                }}
                                icon={<MaterialCommunityIcons name="eye" />} />
                        }
                        placeholder="********"
                        onChangeText={value => updateField('password', value)} />
                    {'password' in errors ? <FormControl.ErrorMessage>{errors['password']}</FormControl.ErrorMessage> : null}

                    <FormControl.Label>{getText('emailLabel')}</FormControl.Label>
                    <Input
                        style={inputStyle.nativeInput}
                        isInvalid={'email' in errors}
                        placeholder={getText('emailPlaceholder')}
                        onChangeText={value => updateField('email', value)} />
                    {'email' in errors ? <FormControl.ErrorMessage>{errors['email']}</FormControl.ErrorMessage> : null}

                    <FormControl.Label>{getText('phoneNumberLabel')}</FormControl.Label>
                    <Input
                        style={inputStyle.nativeInput}
                        isInvalid={'phoneNumber' in errors}
                        placeholder={getText('phoneNumberPlaceholder')}
                        onSubmitEditing={onSubmit}
                        onChangeText={value => updateField('phoneNumber', value)} />
                    {'phoneNumber' in errors ? <FormControl.ErrorMessage>{errors['phoneNumber']}</FormControl.ErrorMessage> : null}
                </FormControl>
                <Button onPress={onSubmit} mt="5">
                    {getText('signUp')}
                </Button>
                {'formError' in errors ? <Text color="red.500" mt="2">{errors['formError'] || ""}</Text> : null}
            </VStack>

            <Button variant={"link"} color={'#000'} onPress={handleAccountExists}>
                {getText('alreadyHaveAccount')}
            </Button>
        </>
    );

    const renderConfirmationForm = () => (
        <>
            <InweonLogo title={getText('confirmAccountTitle')} />
            <VStack width={formProps.width}
                style={containerStyle.formContainer} mx="3">
                <Text mb="4" textAlign="center" color="gray.600">
                    We've sent a confirmation code to {formData.phoneNumber}. Please enter it below to complete your account setup.
                </Text>
                <FormControl isRequired>
                    <FormControl.Label>{getText('confirmationCodeLabel')}</FormControl.Label>
                    <Input
                        style={inputStyle.nativeInput}
                        isInvalid={'confirmationCode' in errors}
                        placeholder="********"
                        onSubmitEditing={onSubmit}
                        onChangeText={value => updateField('confirmationCode', value)} />
                    {'confirmationCode' in errors ? <FormControl.ErrorMessage>{errors['confirmationCode']}</FormControl.ErrorMessage> : null}
                </FormControl>
                <Button onPress={onSubmit} mt="5">
                    {getText('confirmAccount')}
                </Button>
                <Button
                    variant="link"
                    isDisabled={resendCooldown > 0 || showLoadingIndicator}
                    onPress={handleResendCode}>
                    {resendCooldown > 0
                        ? getText('resendConfirmationCodeIn', { seconds: resendCooldown })
                        : getText('resendConfirmationCode')}
                </Button>
                {resendMessage ? <Text color="green.600" mt="2">{resendMessage}</Text> : null}
                {'formError' in errors ? <Text color="red.500" mt="2">{errors['formError'] || ""}</Text> : null}
            </VStack>

            <Button variant={"link"} color={'#000'} onPress={handleBackToSignup}>
                {getText('backToSignUp')}
            </Button>
        </>
    );

    const getProgressText = () => {
        if (currentStep === SignupStep.SIGNUP_FORM) {
            return "Creating your account...";
        } else if (currentStep === SignupStep.CONFIRMATION) {
            return "Confirming your account...";
        }
        return "Processing...";
    };

    return (
        <Box variant={"main"} style={containerStyle.mainContainer}>
            <ProgressBar isVisible={showLoadingIndicator} progressText={getProgressText()} />
            <ScrollView automaticallyAdjustKeyboardInsets={true}>
                {currentStep === SignupStep.SIGNUP_FORM && renderSignupForm()}
                {currentStep === SignupStep.CONFIRMATION && renderConfirmationForm()}
            </ScrollView>
        </Box>
    );
}
